import type {
  CertificateAiWorkflow,
  Certification,
  CertificationLinkedStylesResponse,
  CertificationStandard,
  CertificationStylesPage,
  CertificationsDownloadResponse,
  CertificationsListResponse,
  UpdateCertificationPayload,
} from '@/api/certifications/types';
import type { CreateCertificationPayload } from '@/api/network/types';
import { API_BASE_URL, API_ROOT_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

export async function fetchCertifications(
  qs: string,
): Promise<CertificationsListResponse> {
  return apiFetch<CertificationsListResponse>(`/claims?${qs}`, {}, BACKEND_URL);
}

export async function bulkArchiveCertifications(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/api/claims/bulk-archive',
    { method: 'PATCH', body: JSON.stringify({ ids }) },
    API_ROOT_URL,
  );
}

export async function downloadCertifications(
  ids: string[],
): Promise<CertificationsDownloadResponse> {
  return apiFetch<CertificationsDownloadResponse>(
    '/company-certifications/download',
    { method: 'POST', body: JSON.stringify(ids) },
    BACKEND_URL,
  );
}

export async function exportCertifications(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/claims/export',
    { method: 'POST', body: JSON.stringify({ ids }) },
    BACKEND_URL,
  );
}

export async function triggerCertificateAiWorkflow(
  fileId: string,
): Promise<{ data: CertificateAiWorkflow }> {
  return apiFetch<{ data: CertificateAiWorkflow }>(
    `/certificate-ai/${fileId}`,
    { method: 'POST', body: JSON.stringify({}) },
    BACKEND_URL,
  );
}

export async function getCertificateAiWorkflow(
  workflowId: string,
): Promise<{ data: CertificateAiWorkflow }> {
  return apiFetch<{ data: CertificateAiWorkflow }>(
    `/certificate-ai/${workflowId}`,
    {},
    BACKEND_URL,
  );
}

export async function cancelCertificateAiWorkflow(
  workflowId: string,
): Promise<void> {
  await apiFetch<void>(
    '/certificate-ai/cancel',
    { method: 'DELETE', body: JSON.stringify({ workflowId }) },
    BACKEND_URL,
  );
}

export async function fetchCertificationStandards(): Promise<{
  data: CertificationStandard[];
}> {
  return apiFetch<{ data: CertificationStandard[] }>(
    '/api/certifications',
    {},
    API_ROOT_URL,
  );
}

export async function fetchCertificationById(
  id: string,
): Promise<{ data: Certification }> {
  return apiFetch<{ data: Certification }>(`/claims/${id}`, {}, BACKEND_URL);
}

export async function fetchCertificationVersionHistory(
  id: string,
  qs: string,
): Promise<CertificationsListResponse> {
  return apiFetch<CertificationsListResponse>(
    `/claims/${id}/list-expired-versions?${qs}`,
    {},
    BACKEND_URL,
  );
}

export async function createCertification(
  payload: CreateCertificationPayload,
): Promise<{ data: { id: string } }> {
  return apiFetch<{ data: { id: string } }>(
    '/claims',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

/**
 * Creates a certificate as the answer to a questionnaire CERTIFICATE question.
 *
 * Carries **no company id**: the server files the certificate on the company the
 * answer request is about. `createCertification` takes it in the body, writes the
 * claim and copies its file into private storage, and only *then* reads the row
 * back through a network-scoped query — so a delegated answering party got a 404
 * after the write and left an orphan claim behind, one per retry (DELTA-2415).
 *
 * Styles, renewal and `status` are not part of this contract; the questionnaire
 * dialog does not offer them, so nothing a caller fills in is dropped.
 */
export async function createQuestionnaireRequestCertificate(
  requestId: string,
  payload: {
    standardId: string;
    issueDate: string;
    expirationDate: string;
    fileId?: string | null;
    issuedBy?: string | null;
    documentNumber?: string | null;
    licenseNumber?: string | null;
    explanation?: string | null;
  },
): Promise<{ data: { id: string } }> {
  return apiFetch<{ data: { id: string } }>(
    `/api/questionnaires/requests/${requestId}/certificates`,
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function updateCertification(
  id: string,
  payload: UpdateCertificationPayload,
): Promise<void> {
  await apiFetch<void>(
    `/claims/${id}`,
    { method: 'PUT', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function submitCertificationForReview(id: string): Promise<void> {
  await apiFetch<void>(
    `/api/claims/${id}/request`,
    { method: 'PUT', body: JSON.stringify({}) },
    API_ROOT_URL,
  );
}

export async function fetchCertificationLinkedStyles(
  id: string,
  qs: string,
): Promise<CertificationLinkedStylesResponse> {
  return apiFetch<CertificationLinkedStylesResponse>(
    `/claims/${id}/list-styles?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function fetchStylesList(
  body: object,
): Promise<CertificationStylesPage> {
  return apiFetch(
    '/styles/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export function fetchVariantsList(
  body: object,
): Promise<CertificationStylesPage> {
  return apiFetch(
    '/style-variants/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export function fetchSkusList(body: object): Promise<CertificationStylesPage> {
  return apiFetch(
    '/variant-skus/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function validateCertificationStandard(
  standardId: string,
  companyId: string,
): Promise<
  Array<{
    validationResult: {
      valid: boolean;
      description: Array<Array<{ type: string; text: string }>>;
    };
  }>
> {
  return apiFetch(
    '/v3/certifications/validate',
    {
      method: 'POST',
      body: JSON.stringify([{ certificateId: standardId, companyId }]),
    },
    API_BASE_URL,
  );
}

export async function fetchExistingCertificates(
  standardId: string,
  companyId: string,
): Promise<{
  data: Array<{ id: string; name: string; documentNumber?: string | null }>;
}> {
  return apiFetch<{
    data: Array<{ id: string; name: string; documentNumber?: string | null }>;
  }>(
    `/claims?isDropDown=true&standardId=${encodeURIComponent(standardId)}&companyId=${encodeURIComponent(companyId)}`,
    { method: 'GET' },
    BACKEND_URL,
  );
}

export async function deleteCertification(id: string): Promise<void> {
  await apiFetch<void>(`/claims/${id}`, { method: 'DELETE' }, BACKEND_URL);
}

export async function archiveCertification(id: string): Promise<void> {
  await apiFetch<void>(
    `/api/claims/${id}/to-archive`,
    { method: 'PUT', body: JSON.stringify({}) },
    API_ROOT_URL,
  );
}

export async function unarchiveCertification(id: string): Promise<void> {
  await apiFetch<void>(
    `/api/claims/${id}/to-unarchive`,
    { method: 'PUT', body: JSON.stringify({}) },
    API_ROOT_URL,
  );
}
